import React, { useState, useEffect, useCallback } from 'react';
import { Avatar, Spinner, AvatarProps } from '@chakra-ui/react';
import { useAuth } from '@/contexts/AuthContext';
import { CordeCommonModelsDtoUserprofileAvatar as AvatarDto } from '@/api/generated/v1/api';
import axios from 'axios';

interface CachedAvatarProps extends Omit<AvatarProps, 'src'> {
  profileId: string;
  avatarId?: string;
  avatar?: AvatarDto;
  fallbackSrc?: string;
  onLoadError?: (error: Error) => void;
  onLoadSuccess?: (url: string) => void;
}

// Get base URL from environment
const getBaseUrl = (): string => {
  return import.meta.env.VITE_API_URL || 'http://localhost:8080';
};

export const CachedAvatar: React.FC<CachedAvatarProps> = ({
  profileId,
  avatarId,
  avatar,
  fallbackSrc = '/default-avatar.png',
  onLoadError,
  onLoadSuccess,
  ...avatarProps
}) => {
  const [imageUrl, setImageUrl] = useState<string>(fallbackSrc);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const { accessToken } = useAuth();

  const loadAvatarUrl = useCallback(async () => {
    const targetAvatarId = avatarId || avatar?.id;
    
    console.log('CachedAvatar: Loading avatar', { profileId, targetAvatarId, avatar });
    
    if (!targetAvatarId) {
      console.log('CachedAvatar: No avatar ID, using fallback');
      setImageUrl(fallbackSrc);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setHasError(false);
      
      // Делаем авторизованный запрос для получения бинарных данных аватара
      const baseUrl = getBaseUrl();
      const avatarUrl = `${baseUrl}/api/v1/userprofile/${profileId}/avatars/${targetAvatarId}`;
      
      console.log('CachedAvatar: Fetching avatar from URL', avatarUrl);
      
      const response = await axios.get(avatarUrl, {
        responseType: 'blob', // Важно для бинарных данных
        headers: {
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
      });

      // Создаем blob URL из бинарных данных
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] || 'image/png' 
      });
      const objectUrl = URL.createObjectURL(blob);
      
      console.log('CachedAvatar: Successfully loaded avatar blob', objectUrl);
      setImageUrl(objectUrl);
      onLoadSuccess?.(objectUrl);
      
    } catch (error) {
      console.error('CachedAvatar: Failed to load avatar:', error);
      setHasError(true);
      setImageUrl(fallbackSrc);
      onLoadError?.(error as Error);
    } finally {
      setIsLoading(false);
    }
  }, [profileId, avatarId, avatar?.id, fallbackSrc, onLoadError, onLoadSuccess, accessToken]);

  useEffect(() => {
    loadAvatarUrl();
  }, [loadAvatarUrl]);

  // Reset state when avatar changes
  useEffect(() => {
    setHasError(false);
    // Очищаем старый blob URL
    if (imageUrl && imageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imageUrl);
    }
    setImageUrl(fallbackSrc);
  }, [profileId, avatarId, avatar?.id, fallbackSrc]);

  // Очищаем blob URL при размонтировании компонента
  useEffect(() => {
    return () => {
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  if (isLoading) {
    return (
      <Avatar {...avatarProps}>
        <Spinner size="sm" />
      </Avatar>
    );
  }

  return (
    <Avatar
      {...avatarProps}
      src={hasError ? fallbackSrc : imageUrl}
      onError={() => {
        if (!hasError) {
          console.log('Avatar image failed to load, falling back to default');
          setHasError(true);
          setImageUrl(fallbackSrc);
        }
      }}
    />
  );
};

export default CachedAvatar; 